import React, { useCallback, useEffect } from "react";
import "./provider-modal.css";
import { IConfig } from "../../pages/ChatWindow";

interface ProviderModalProps {
	config: IConfig;
	onClose: () => void;
	onProviderSelect: () => void;
	onPatientSelect: () => void;
}

export const ProviderModal: React.FC<ProviderModalProps> = ({
	config,
	onClose,
	onProviderSelect,
	onPatientSelect,
}) => {
	// Use useCallback for handlers to prevent unnecessary re-renders - MUST be before any conditional returns
	const handleProviderClick = useCallback(() => {
		try {
			onProviderSelect();
			onClose();
		} catch (error) {
			console.error("Error in provider selection", error);
		}
	}, [onProviderSelect, onClose]);

	const handlePatientClick = useCallback(() => {
		try {
			onPatientSelect();
			onClose();
		} catch (error) {
			console.error("Error in patient selection", error);
		}
	}, [onPatientSelect, onClose]);

	// Add keyboard accessibility - MUST be before any conditional returns
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent): void => {
			if (e.key === "Escape") {
				onClose();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [onClose]);

	// Error handling - ensure config exists (after all hooks are called)
	if (!config) {
		console.error("Provider modal rendered without config");
		return null;
	}

	// Apply theme colors based on the config with fallbacks for type safety
	const modalHeaderStyle = {
		backgroundColor: config.brand_primary_bg_color || "#FFFFFF",
		color: config.brand_primary_text_color || "#000000",
	};

	const providerButtonStyle = {
		backgroundColor: config.brand_secondary_bg_color || "#EEEEEE",
		color: config.brand_secondary_text_color || "#000000",
	};

	const patientButtonStyle = {
		backgroundColor: config.brand_tertiary_bg_color || "#DDDDDD",
		color: config.brand_tertiary_text_color || "#000000",
	};

	return (
		<div className="provider-modal-overlay">
			<div className="provider-modal-container">
				<div className="provider-modal-content">
					{/* <div className="provider-modal-header" style={modalHeaderStyle}></div> */}

					<div className="provider-modal-body">
						<p className="provider-modal-text">
							The information provided on{" "}
							{window.location.hostname || "this website"} is intended for U.S.
							Healthcare Professionals and Patients/Caregivers only. Please
							confirm you are a U.S. Healthcare Professional or
							Patient/Caregiver.
						</p>

						<div className="provider-modal-buttons">
							<button
								className="provider-modal-button provider-button"
								style={providerButtonStyle}
								onClick={handleProviderClick}
							>
								I AM A HEALTHCARE PROVIDER
							</button>

							<button
								className="provider-modal-button patient-button"
								style={patientButtonStyle}
								onClick={handlePatientClick}
							>
								I AM A PATIENT/CAREGIVER
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
